import { createContext, useContext, useState, useEffect, ReactNode, FC } from "react";

interface DeviceState {
  isIOS: boolean;
  isMac: boolean;
  isSafari: boolean;
}

interface DeviceContextValue extends DeviceState { }

interface DeviceProviderProps {
  children: ReactNode;
}

const DeviceContext = createContext<DeviceContextValue | undefined>(undefined);

export const useDevice = (): DeviceContextValue => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  return context;
};

const detectDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const isIOSUserAgent = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const hasTouchScreen = (): boolean => {
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 1)
    );
  };

  const isMacWithTouch = /Mac/.test(navigator.userAgent) && hasTouchScreen();

  return isIOSUserAgent || isMacWithTouch;
};

const detectMac = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  return (
    /Mac/.test(userAgent) &&
    !/iPhone|iPad|iPod/.test(userAgent) &&
    !/Android/.test(userAgent)
  );
};

const detectSafari = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isChrome = /chrome|chromium|crios/i.test(userAgent);
  const isSafari = /safari/i.test(userAgent);

  return isSafari && !isChrome;
};

const getInitialDeviceState = (): DeviceState => ({
  isIOS: detectDevice(),
  isMac: detectMac(),
  isSafari: detectSafari(),
});

export const DeviceProvider: FC<DeviceProviderProps> = ({ children }) => {
  const [deviceState, setDeviceState] = useState<DeviceState>(getInitialDeviceState);

  useEffect(() => {
    const updateDeviceState = () => {
      setDeviceState({
        isIOS: detectDevice(),
        isMac: detectMac(),
        isSafari: detectSafari(),
      });
    };

    updateDeviceState();

    const handleResize = () => {
      updateDeviceState();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <DeviceContext.Provider value={deviceState}>
      {children}
    </DeviceContext.Provider>
  );
};